import {memo, useState} from 'react'
import {format} from 'date-fns'
import {Text} from '@/components/ui/text/Text'
import {
    StyledMessageWrapper,
    StyledMessageItem,
    StyledUrgentTagIcon,
    StyledMessageText,
    StyledDeletedMessageText
} from './style.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Button} from '@/components/ui/button/Button'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useTranslation} from 'react-i18next'
import {ConversationMessage} from '../../types.ts'
import {useAuthStore} from '@/features/auth/store/store.ts'
import {DeleteMessageModal} from '../delete-message-modal/DeleteMessageModal'

interface MessageBubbleProps {
    message: ConversationMessage
    /** True only for the conversation's last non-deleted message when sent by the current user */
    canDelete?: boolean
}

const MessageBubbleComponent = ({message, canDelete = false}: MessageBubbleProps) => {
    const {t} = useTranslation()
    const currentUser = useAuthStore(state => state.user)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const isOwnMessage = message.senderId === currentUser?.id
    const {sender, createdAt, isUrgent, message: messageText, deletedAt} = message
    const isDeleted = !!deletedAt
    const senderName = `${sender.firstName} ${sender.lastName}`

    return (
        <>
            <StyledMessageWrapper $isOwnMessage={isOwnMessage} fullWidth>
                <Avatar size="xs" name={senderName} imageUrl={sender.avatarUrl || undefined} />
                <StyledMessageItem $isOwnMessage={isOwnMessage}>
                    <Flexbox direction="row" gap={1} align="center" justify="space-between">
                        <Text size="textXs" palette="neutral" shade="800" weight="600">
                            {senderName}
                        </Text>
                        <Flexbox direction="row" gap={1} align="center">
                            <Text size="textXs" palette="neutral" shade="500">
                                {format(new Date(createdAt), 'HH:mm')}
                            </Text>
                            {/* A deleted message keeps its `isUrgent` flag in the payload, but the
                                urgency no longer applies to anything the user can read */}
                            {isUrgent && !isDeleted && <StyledUrgentTagIcon size={16} />}
                            {canDelete && !isDeleted && (
                                <Tooltip
                                    variant="light"
                                    content={t('conversations:delete_message_title')}
                                    triggerProps={{asChild: true}}
                                >
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        shape="square"
                                        aria-label={t('conversations:delete_message_title')}
                                        onClick={() => setIsDeleteModalOpen(true)}
                                    >
                                        <Trash01Icon size={14} />
                                    </Button>
                                </Tooltip>
                            )}
                        </Flexbox>
                    </Flexbox>
                    {isDeleted ? (
                        <StyledDeletedMessageText size="textSm" palette="neutral" shade="500">
                            {t('conversations:message_deleted_placeholder')}
                        </StyledDeletedMessageText>
                    ) : (
                        <StyledMessageText size="textSm" palette="neutral" shade="900">
                            {messageText}
                        </StyledMessageText>
                    )}
                </StyledMessageItem>
            </StyledMessageWrapper>

            {isDeleteModalOpen && (
                <DeleteMessageModal
                    conversationId={message.conversationId}
                    messageId={message.id}
                    onClose={() => setIsDeleteModalOpen(false)}
                />
            )}
        </>
    )
}

export const MessageBubble = memo(MessageBubbleComponent)
